/**
 * NmiChangeModal
 *
 * Reusable modal for submitting NMI / SN change approval requests.
 *
 * Props:
 *   open            {bool}     - controls visibility
 *   onClose         {fn}       - called when modal is dismissed
 *   onSuccess       {fn}       - called after at least one request is submitted successfully
 *   prefilledSystem {object}   - optional: { id, sn, nmi, controller_name }
 *                                When provided the system selector is hidden and the
 *                                system fields are shown as read-only current values.
 */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import api from "@/libs/api";

const { Text } = Typography;
const { TextArea } = Input;

export default function NmiChangeModal({ open, onClose, onSuccess, prefilledSystem }) {
  const [form] = Form.useForm();
  const [systems, setSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // When a prefilled system is provided, use it as the active system
  const activeSystem = prefilledSystem || selectedSystem;

  // ── Reset on open ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setSelectedSystem(null);

    if (prefilledSystem) {
      // Lock the system — set hidden field value
      form.setFieldsValue({ system_id: prefilledSystem.id });
      // Pre-check for existing pending requests and show inline errors immediately
      checkPendingRequests(prefilledSystem.id);
    } else {
      // Need to let user pick — fetch system list
      fetchSystems();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPendingRequests = async (systemId) => {
    try {
      const res = await api.get(
        `/api/approval-requests?status=PENDING&entity_id=${systemId}`
      );
      if (!res.data.success) return;
      const pending = res.data.data?.pending || [];
      if (pending.length === 0) return;

      const fieldErrors = [];
      for (const req of pending) {
        const f = req.payload?.field;
        if (f === "sn") {
          fieldErrors.push({
            name: "new_sn",
            errors: [
              "A pending SN change request already exists for this system. You can only have maximum of one change requested at a time.",
            ],
          });
        } else if (f === "nmi") {
          fieldErrors.push({
            name: "new_nmi",
            errors: [
              "A pending NMI change request already exists for this system. You can only have maximum of one change requested at a time.",
            ],
          });
        }
      }
      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors);
      }
    } catch (err) {
      console.error("Error checking pending requests:", err);
    }
  };

  const fetchSystems = async () => {
    setSystemsLoading(true);
    try {
      const res = await api.get("/api/systems/");
      if (res.data.success) {
        setSystems(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching systems:", err);
    } finally {
      setSystemsLoading(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSystemSelect = (systemId) => {
    const sys = systems.find((s) => s.id === systemId);
    setSelectedSystem(sys || null);
    form.setFieldsValue({ new_sn: undefined, new_nmi: undefined });
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedSystem(null);
    onClose();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const { system_id, new_sn, new_nmi, reason } = values;
    const requests = [];

    if (new_sn && new_sn.trim()) requests.push({ field: "sn", new_value: new_sn.trim() });
    if (new_nmi && new_nmi.trim()) requests.push({ field: "nmi", new_value: new_nmi.trim() });

    if (requests.length === 0) {
      message.error("Please enter at least one new SN or NMI value");
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    const toastErrors = [];

    for (const req of requests) {
      try {
        const res = await api.post("/api/approval-requests", {
          entity_type: "SYSTEM",
          entity_id: system_id,
          action_type: "CHANGE_FIELD",
          payload: { field: req.field, new_value: req.new_value },
          requester_note: reason || undefined,
        });
        if (res.data.success) {
          successCount++;
        } else {
          toastErrors.push(`${req.field.toUpperCase()}: ${res.data.message || "Failed"}`);
        }
      } catch (err) {
        const errData = err.response?.data || {};
        const msg = errData.message || "Network error";
        if (errData.field) {
          const formField = errData.field === "sn" ? "new_sn" : "new_nmi";
          form.setFields([{ name: formField, errors: [msg] }]);
        } else {
          toastErrors.push(`${req.field.toUpperCase()}: ${msg}`);
        }
      }
    }

    setSubmitting(false);

    if (successCount > 0) {
      message.success(
        `${successCount} request${successCount > 1 ? "s" : ""} submitted successfully`
      );
    }
    toastErrors.forEach((e) => message.error(e));

    if (successCount > 0) {
      handleClose();
      if (onSuccess) onSuccess();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          <span>New NMI / SN Change Request</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
          Submit Request
        </Button>,
      ]}
      width={560}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* System selector — hidden when a system is pre-filled from parent */}
        {!prefilledSystem && (
          <Form.Item
            name="system_id"
            label="System"
            rules={[{ required: true, message: "Please select a system" }]}
          >
            <Select
              showSearch
              placeholder="Search by SN or NMI..."
              loading={systemsLoading}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleSystemSelect}
              options={systems.map((s) => ({
                value: s.id,
                label: `${s.sn} — ${s.nmi || "No NMI"}`,
              }))}
            />
          </Form.Item>
        )}

        {/* Hidden field when prefilled — keeps system_id in form values */}
        {prefilledSystem && (
          <Form.Item name="system_id" hidden>
            <Input />
          </Form.Item>
        )}

        {/* Current SN / NMI info card */}
        {activeSystem && (
          <div
            style={{
              background: "#fafafa",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
              border: "1px solid #f0f0f0",
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  CURRENT SN
                </Text>
                <div style={{ fontWeight: 600, marginTop: 2 }}>{activeSystem.sn || "-"}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  CURRENT NMI
                </Text>
                <div style={{ fontWeight: 600, marginTop: 2 }}>{activeSystem.nmi || "-"}</div>
              </Col>
            </Row>
          </div>
        )}

        {/* New SN */}
        <Form.Item
          name="new_sn"
          label="New Serial Number (SN)"
          extra="Leave blank if not changing SN"
          dependencies={["new_nmi"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  (value && value.trim()) ||
                  (getFieldValue("new_nmi") && getFieldValue("new_nmi").trim())
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Enter at least one new SN or NMI value"));
              },
            }),
            {
              validator(_, value) {
                if (!value || !value.trim()) return Promise.resolve();
                if (
                  activeSystem?.controller_name?.toLowerCase() === "powow meter" &&
                  !/^TSM\d{10}$/.test(value.trim())
                ) {
                  return Promise.reject(
                    new Error(
                      "Powow meter SN must start with 'TSM' followed by exactly 10 digits"
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="e.g. TSM1234567890 (Powow meter)"
            style={{ borderRadius: 2 }}
            onChange={() => form.validateFields(["new_nmi"])}
          />
        </Form.Item>

        {/* New NMI */}
        <Form.Item
          name="new_nmi"
          label="New NMI"
          extra="Leave blank if not changing NMI. Must be 10 or 11 letters/digits."
          dependencies={["new_sn"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  (value && value.trim()) ||
                  (getFieldValue("new_sn") && getFieldValue("new_sn").trim())
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Enter at least one new SN or NMI value"));
              },
            }),
            {
              validator(_, value) {
                if (!value || !value.trim()) return Promise.resolve();
                const compact = value.replace(/\s/g, "").toUpperCase();
                if (!/^[A-Z0-9]{10,11}$/.test(compact)) {
                  return Promise.reject(new Error("NMI must be 10 or 11 letters/digits"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="10 or 11 character NMI"
            style={{ borderRadius: 2 }}
            onChange={() => form.validateFields(["new_sn"])}
          />
        </Form.Item>

        {/* Reason */}
        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: "Please provide a reason for this change" }]}
        >
          <TextArea
            rows={3}
            placeholder="Why is this change needed?"
            style={{ borderRadius: 2 }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
